import { useState, useCallback } from 'react';
import { getSentence } from '../components/translations';

/**
 * useModal - Custom hook for managing modal state
 * 
 * @param {boolean} initialOpen - Initial open state
 * @returns {Object} Modal state and handlers
 */
export const useModal = (initialOpen = false) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);

  const open = useCallback((modalData = null) => {
    setData(modalData);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
    // Clear data after a short delay to allow for exit animations
    setTimeout(() => setData(null), 300);
  }, []);

  const setLoading = useCallback((loading) => {
    setIsLoading(loading);
  }, []);

  const updateData = useCallback((newData) => {
    setData(prevData => ({ ...prevData, ...newData }));
  }, []);

  return {
    isOpen,
    isLoading,
    data,
    open,
    close,
    setLoading,
    updateData
  };
};

/**
 * useConfirmationModal - Specialized hook for confirmation dialogs
 * 
 * @returns {Object} Confirmation modal state and handlers
 */
export const useConfirmationModal = () => {
  const modal = useModal();
  
  const confirm = useCallback((options = {}) => {
    const {
      title = getSentence(148),
      message = getSentence(147),
      confirmText = getSentence(9),
      cancelText = getSentence(8),
      onConfirm,
      onCancel,
      ...otherOptions
    } = options;
    
    modal.open({
      title,
      message,
      confirmText,
      cancelText,
      onConfirm: async () => {
        if (onConfirm) {
          modal.setLoading(true);
          try {
            await onConfirm();
            modal.close();
          } catch (error) {
            console.error('Confirmation action failed:', error);
            modal.setLoading(false);
          }
        } else {
          modal.close();
        }
      },
      onCancel: () => {
        if (onCancel) onCancel();
        modal.close();
      },
      ...otherOptions
    });
  }, [modal]);

  return {
    ...modal,
    confirm
  };
};

/**
 * useMultiModal - Specialized hook for managing multiple modals (like auth modals)
 * Used specifically for LandingPage authentication flow
 * 
 * @returns {Object} Multi-modal state and handlers
 */
export const useMultiModal = () => {
  const [modals, setModals] = useState({
    signup: false,
    signin: false,
    forgotPassword: false,
    background: false
  });

  const openModal = useCallback((modalName) => {
    setModals(prev => ({
      ...prev,
      [modalName]: true,
      background: true
    }));
  }, []);

  const closeModal = useCallback((modalName) => {
    setModals(prev => {
      const newState = { ...prev, [modalName]: false };
      // Check if any modal (except background) is still open
      const hasOpenModal = Object.entries(newState).some(([key, value]) => 
        key !== 'background' && value === true
      );
      return {
        ...newState,
        background: hasOpenModal
      };
    });
  }, []);

  const switchModal = useCallback((fromModal, toModal) => {
    setModals(prev => ({
      ...prev,
      [fromModal]: false,
      [toModal]: true,
      background: true
    }));
  }, []);

  const closeAll = useCallback(() => {
    setModals({
      signup: false,
      signin: false,
      forgotPassword: false,
      background: false
    });
  }, []);

  // Individual modal handlers
  const openSignup = useCallback(() => openModal('signup'), [openModal]);
  const openSignin = useCallback(() => openModal('signin'), [openModal]);
  const openForgotPassword = useCallback(() => openModal('forgotPassword'), [openModal]);

  const closeSignup = useCallback(() => closeModal('signup'), [closeModal]);
  const closeSignin = useCallback(() => closeModal('signin'), [closeModal]);
  const closeForgotPassword = useCallback(() => closeModal('forgotPassword'), [closeModal]);

  return {
    modals,
    openSignup,
    openSignin,
    openForgotPassword,
    closeSignup,
    closeSignin,
    closeForgotPassword,
    switchModal,
    closeAll
  };
};